import { forwardRef, useMemo } from 'react';
import { Badge as MuiBadge, BadgeProps as MuiBadgeProps } from '@mui/material';

/**
 * Props for the Badge component.
 *
 * Extends all MUI Badge props with enhanced accessibility features.
 *
 * @example
 * ```tsx
 * // Notification count badge
 * <Badge badgeContent={5} color="primary">
 *   <NotificationsIcon />
 * </Badge>
 *
 * // Status indicator (dot)
 * <Badge variant="dot" color="success">
 *   <Avatar />
 * </Badge>
 *
 * // With custom aria-label
 * <Badge badgeContent={10} aria-label="10 unread messages">
 *   <MailIcon />
 * </Badge>
 * ```
 */
export interface BadgeProps extends MuiBadgeProps {
  /**
   * Accessible label for screen readers.
   * If not provided, automatically generated based on badgeContent and variant:
   * - Numbers: "X notification(s)" with singular/plural handling
   * - Text: "{text} notification"
   * - Dot variant: "Status indicator"
   * - Fallback: "Notification badge"
   */
  'aria-label'?: string;
}

/**
 * Badge component for displaying notification indicators and status overlays.
 *
 * A thin wrapper around MUI Badge with enhanced accessibility features including
 * automatic aria-label generation based on content type and proper screen reader support.
 *
 * Features:
 * - Multiple variants: standard (with content), dot (minimal indicator)
 * - Theme-aware colors: default, primary, secondary, error, success, warning, info
 * - Flexible positioning: 8 anchorOrigin combinations (top/bottom × left/right)
 * - Overlap modes: rectangular, circular
 * - Number truncation: Use max prop to show "99+" for large counts
 * - Zero handling: Control visibility with showZero prop
 * - Enhanced accessibility: Auto-generated aria-labels with singular/plural support
 * - Full theme integration
 *
 * Common Use Cases:
 * - Notification counts on navigation items
 * - Work task counters
 * - Status indicators (online/offline, active/inactive)
 * - Unread message counts
 * - Shopping cart item counts
 *
 * @component
 * @example
 * ```tsx
 * // Simple notification badge
 * <Badge badgeContent={4} color="primary">
 *   <IconButton>
 *     <NotificationsIcon />
 *   </IconButton>
 * </Badge>
 *
 * // Status indicator (dot)
 * <Badge variant="dot" color="success">
 *   <Avatar src="/avatar.jpg" />
 * </Badge>
 *
 * // Work count badge (from mobile app)
 * <Badge badgeContent={workCount} color="error" variant="dot">
 *   <WorkIcon />
 * </Badge>
 *
 * // Truncated large numbers
 * <Badge badgeContent={1000} max={99} color="secondary">
 *   <MailIcon />
 * </Badge>
 *
 * // Custom positioning
 * <Badge
 *   badgeContent={3}
 *   anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
 *   color="warning"
 * >
 *   <FolderIcon />
 * </Badge>
 *
 * // Hide when zero
 * <Badge badgeContent={0} showZero={false}>
 *   <ShoppingCartIcon />
 * </Badge>
 * ```
 */
export const Badge = forwardRef<HTMLSpanElement, BadgeProps>(
  ({ badgeContent, variant = 'standard', 'aria-label': ariaLabel, ...props }, ref) => {
    // Auto-generate aria-label if not provided for better accessibility
    const defaultAriaLabel = useMemo(() => {
      // Always use explicit aria-label if provided
      if (ariaLabel) return ariaLabel;

      // Dot variant represents status/presence indicator
      if (variant === 'dot') {
        return 'Status indicator';
      }

      // Number content: use singular/plural handling
      if (typeof badgeContent === 'number') {
        return `${badgeContent} notification${badgeContent !== 1 ? 's' : ''}`;
      }

      // Text content: announce as notification
      if (typeof badgeContent === 'string') {
        return `${badgeContent} notification`;
      }

      // Fallback for React nodes or other content
      return 'Notification badge';
    }, [badgeContent, variant, ariaLabel]);

    return (
      <MuiBadge
        ref={ref}
        badgeContent={badgeContent}
        variant={variant}
        aria-label={defaultAriaLabel}
        {...props}
      />
    );
  }
);

Badge.displayName = 'Badge';
